<template>
    <ExpandableCard
        :expand-id="deportista.id"
        @expand="emit('expand', $event)"
    >
        <template #summaryIcon>
            <div class="tw:hidden tw:w-10 tw:h-10 tw:rounded-full tw:border tw:border-slate-100 tw:sm:flex tw:items-center tw:justify-center tw:text-[var(--altcolor)]">
                <i class="material-icons tw:text-xl">directions_run</i>
            </div>
        </template>

        <template #summaryContent>
            <div>
                <h2 class="tw:text-base tw:font-extrabold tw:text-slate-900">{{ nombreCompleto }}</h2>
                <p v-if="summaryContext" class="tw:text-slate-500 tw:text-sm tw:font-medium tw:m-0">
                    {{ summaryContext }}
                </p>
            </div>
        </template>

        <template #summaryActions>
            <div v-if="summaryBadges.length" class="tw:hidden tw:sm:flex tw:flex-wrap tw:justify-end tw:gap-2">
                <BadgeFront
                    v-for="badge in summaryBadges"
                    :key="badge.key"
                    :variant="badge.variant"
                    :label="badge.label"
                    :icon="badge.icon"
                    :dot="false"
                />
            </div>

            <div v-if="summaryBadges.length" class="tw:flex tw:sm:hidden">
                <BadgeFront
                    :variant="summaryBadges[0].variant"
                    :label="summaryBadges[0].label"
                    :icon="summaryBadges[0].icon"
                    :dot="false"
                />
            </div>
        </template>

        <div class="tw:flex tw:flex-col tw:gap-5">
            <!-- Botón Descargar PDF — arriba a la derecha -->
            <div v-if="downloadUrl" class="tw:flex tw:justify-end">
                <Button
                    as="a"
                    :href="downloadUrl"
                    target="_blank"
                    variant="alt"
                    icon="material-icons tw:text-lg file_download"
                >
                    {{ labels.downloadPdf }}
                </Button>
            </div>

            <div
                :class="[
                    'tw:grid tw:grid-cols-1 tw:gap-5',
                    ((quickFacts.length || detailBadges.length) && hasPositions) ? 'tw:md:grid-cols-2' : ''
                ]"
            >
                <CardFront v-if="quickFacts.length || detailBadges.length">
                    <div v-if="detailBadges.length" class="tw:flex tw:flex-wrap tw:gap-2 tw:mb-4">
                        <BadgeFront
                            v-for="badge in detailBadges"
                            :key="badge.key"
                            :variant="badge.variant"
                            :label="badge.label"
                            :icon="badge.icon"
                            :dot="false"
                        />
                    </div>

                    <div v-if="quickFacts.length" :class="['tw:grid tw:gap-3', quickFacts.length === 3 ? 'tw:grid-cols-3' : 'tw:grid-cols-2']">
                        <div
                            v-for="fact in quickFacts"
                            :key="fact.key"
                            class="tw:rounded-lg tw:border tw:border-slate-200 tw:bg-white tw:px-4 tw:py-3 tw:shadow-sm"
                        >
                            <p class="tw:mb-2 tw:text-[11px] tw:font-bold tw:uppercase tw:tracking-[0.075em] tw:leading-none tw:text-slate-400">{{ fact.label }}</p>
                            <p class="tw:m-0 tw:text-sm tw:font-bold tw:text-slate-900">{{ fact.value }}</p>
                        </div>
                    </div>
                </CardFront>

                <CardFront v-if="hasPositions" :title="labels.positions">

                    <div v-if="posicionesPrincipales.length" class="tw:flex tw:flex-col tw:gap-3">
                        <p class="tw:m-0 tw:text-sm tw:font-semibold tw:text-slate-500">{{ labels.primaryPositions }}</p>
                        <div class="tw:flex tw:flex-wrap tw:gap-2">
                            <span
                                v-for="position in posicionesPrincipales"
                                :key="`principal-${position}`"
                                class="tw:inline-flex tw:items-center tw:rounded-full tw:bg-[var(--altcolor)]/10 tw:px-3 tw:py-1.5 tw:text-sm tw:font-semibold tw:text-[var(--altcolor)]"
                            >
                                {{ position }}
                            </span>
                        </div>
                    </div>

                    <div v-if="posicionesAlternativas.length" class="tw:flex tw:flex-col tw:gap-3">
                        <p class="tw:m-0 tw:text-sm tw:font-semibold tw:text-slate-500">{{ labels.alternativePositions }}</p>
                        <div class="tw:flex tw:flex-wrap tw:gap-2">
                            <span
                                v-for="position in posicionesAlternativas"
                                :key="`alternativa-${position}`"
                                class="tw:inline-flex tw:items-center tw:rounded-full tw:bg-slate-100 tw:px-3 tw:py-1.5 tw:text-sm tw:font-semibold tw:text-slate-600"
                            >
                                {{ position }}
                            </span>
                        </div>
                    </div>
                </CardFront>
            </div>

            <div
                :class="[
                    'tw:grid tw:grid-cols-1 tw:gap-5',
                    (hasResponsables && hasIndumentarias) ? 'tw:md:grid-cols-2' : ''
                ]"
            >
                <CardFront v-if="hasResponsables" :title="labels.responsables">

                    <div class="tw:flex tw:flex-col tw:gap-3">
                        <div
                            v-for="(responsable, index) in responsables"
                            :key="index"
                            class="tw:flex tw:flex-col tw:gap-3 tw:rounded-lg tw:border tw:border-slate-200 tw:bg-white tw:px-4 tw:py-3"
                        >
                            <div class="tw:flex tw:items-center tw:gap-3">
                                <div class="tw:flex tw:h-9 tw:w-9 tw:flex-shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:bg-white tw:text-[var(--altcolor)] tw:border tw:border-slate-100">
                                    <i class="material-icons tw:text-lg">supervisor_account</i>
                                </div>
                                <p class="tw:m-0 tw:text-sm tw:font-semibold tw:text-slate-900">{{ responsable.nombre }}</p>
                            </div>

                            <div class="tw:grid tw:grid-cols-1 tw:sm:grid-cols-2 tw:gap-2">
                                <div v-if="responsable.dni">
                                    <span class="tw:text-[11px] tw:font-bold tw:uppercase tw:tracking-[0.075em] tw:text-slate-400">{{ labels.dni }}</span>
                                    <p class="tw:m-0 tw:text-sm tw:text-slate-900">{{ responsable.dni }}</p>
                                </div>
                                <div v-if="responsable.email">
                                    <span class="tw:text-[11px] tw:font-bold tw:uppercase tw:tracking-[0.075em] tw:text-slate-400">{{ labels.email }}</span>
                                    <p class="tw:m-0 tw:text-sm tw:text-slate-900 tw:break-all">{{ responsable.email }}</p>
                                </div>
                                <div v-if="responsable.telefono">
                                    <span class="tw:text-[11px] tw:font-bold tw:uppercase tw:tracking-[0.075em] tw:text-slate-400">{{ labels.telefono }}</span>
                                    <p class="tw:m-0 tw:text-sm tw:text-slate-900">{{ responsable.telefono }}</p>
                                </div>
                                <div v-if="responsable.fecha_nacimiento">
                                    <span class="tw:text-[11px] tw:font-bold tw:uppercase tw:tracking-[0.075em] tw:text-slate-400">{{ labels.fecha_nacimiento }}</span>
                                    <p class="tw:m-0 tw:text-sm tw:text-slate-900">{{ responsable.fecha_nacimiento }}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </CardFront>

                <IndumentariaList v-if="hasIndumentarias" :items="indumentarias" />
            </div>

            <CardFront :title="$t('perfil.plazos')">
                <div>
                    <p class="tw:mt-2 tw:mb-0 tw:text-sm tw:text-slate-500">{{ labels.plazosHelp }}</p>
                </div>

                <div
                    v-if="detalleError"
                    class="tw:flex tw:flex-col tw:items-center tw:justify-center tw:gap-3 tw:rounded-md tw:border tw:border-red-200 tw:bg-red-50 tw:px-4 tw:py-6 tw:text-center"
                >
                    <i class="material-icons tw:text-3xl tw:text-red-300">error_outline</i>
                    <p class="tw:m-0 tw:text-sm tw:font-semibold tw:text-red-700">{{ detalleError }}</p>
                    <button
                        type="button"
                        class="tw:inline-flex tw:items-center tw:gap-2 tw:rounded-md tw:border tw:border-red-300 tw:bg-white tw:px-3 tw:py-2 tw:text-sm tw:font-semibold tw:text-red-700 hover:tw:bg-red-100"
                        @click="emit('expand', props.deportista.id)"
                    >
                        <i class="material-icons tw:text-base">refresh</i>
                        {{ labels.retry }}
                    </button>
                </div>

                <Skeleton v-else-if="loadingDetalle || !detalle" customClass="tw:shadow-none">
                    <template #skeleton>
                        <SkeletonItem class="tw:w-1/4 tw:h-4 tw:mb-4" />
                        <div class="tw:flex tw:flex-col tw:gap-3">
                            <SkeletonItem class="tw:w-full tw:h-12" />
                            <SkeletonItem class="tw:w-full tw:h-12" />
                            <SkeletonItem class="tw:w-full tw:h-12" />
                        </div>
                    </template>
                </Skeleton>

                <PlazosList
                    v-else
                    :plazos="detalle.plazos"
                    selectable
                    @plazo-toggled="onPlazoToggled"
                />
            </CardFront>
        </div>
    </ExpandableCard>
</template>

<script setup>
    import { toRef } from 'vue';
    import { useCanteraCard } from '@/composables/front/perfil/useCanteraCard';
    import BadgeFront from '@/modules/front/ui/BadgeFront.vue';
    import Button from '@/modules/front/ui/Button.vue';
    import { Skeleton, SkeletonItem } from '@/modules/front/ui/skeleton';
    import ExpandableCard from '@/modules/ui/ExpandableCard.vue';
    import PlazosList from '../plazos/PlazosList.vue';
    import IndumentariaList from './IndumentariaList.vue';
    import CardFront from '@/modules/front/ui/CardFront.vue';

    const props = defineProps({
        deportista: {
            type: Object,
            required: true,
        },
        /** Datos del detalle — null mientras no se haya cargado */
        detalle: {
            type: Object,
            default: null,
        },
        detalleError: {
            type: String,
            default: null,
        },
        /** true mientras la petición de detalle está en curso para esta card */
        loadingDetalle: {
            type: Boolean,
            default: false,
        },
        /** Función que comprueba si un plazo está seleccionado: (deportistaId, plazoId) => boolean */
        isSelected: {
            type: Function,
            default: () => false,
        },
    });

    const emit = defineEmits(['expand', 'plazo-toggled']);

    const {
        labels,
        nombreCompleto,
        summaryContext,
        summaryBadges,
        detailBadges,
        quickFacts,
        posicionesPrincipales,
        posicionesAlternativas,
        responsables,
        indumentarias,
        hasPositions,
        hasResponsables,
        hasIndumentarias,
        downloadUrl,
    } = useCanteraCard(toRef(props, 'deportista'), toRef(props, 'detalle'));

    const isDeportistaPlazoSelected = plazo => props.isSelected(props.deportista.id, plazo.id);

    const onPlazoToggled = plazo => {
        emit('plazo-toggled', {
            deportistaId: props.deportista.id,
            plazoId: plazo.id,
            estado: plazo.estado,
        });
    };
</script>
